/**
 * Domain induk tempat subdomain lembaga dibuat: `{slug}.{domain}`.
 *
 * Sebelumnya ditulis tetap sebagai `gladhy.app` di layar Platform, padahal
 * produksi memakai `gladhy.co.id`. Akibatnya setiap lembaga baru diserahi
 * alamat yang tidak akan pernah terbuka — dan tidak ada yang menyadarinya,
 * karena tidak ada galat: alamat itu hanya salah, bukan rusak.
 *
 * Sumber utamanya server, lewat `platform_domain` pada GET /settings. Itu satu-
 * satunya jawaban yang bisa dipercaya, sebab menebak dari alamat yang sedang
 * dibuka gagal pada satu keadaan yang benar-benar terjadi: pemilik platform
 * boleh masuk lewat subdomain lembaga mana pun, dan dari `osct.gladhy.co.id`
 * tebakan itu menghasilkan alamat bersarang `baru.osct.gladhy.co.id`.
 *
 * Menebak dari `location` hanya dipakai bila server belum menyebutkannya —
 * praktisnya di komputer pengembang. Bukan variabel lingkungan baru yang bisa
 * lupa diisi: PLATFORM_DOMAIN sudah menopang CORS dan TLS, jadi bila ia kosong
 * di produksi, hal-hal itu sudah rusak lebih dulu.
 */

// Label yang milik platform, bukan nama lembaga. Cerminan RESERVED_LABELS di
// osct_backend/src/middleware/tenant.js — kalau di sana bertambah, di sini juga.
const LABEL_PLATFORM = new Set(['www', 'api', 'app', 'admin', 'static', 'cdn'])

/**
 * @param {string} [dariServer] nilai `platform_domain` dari GET /settings.
 * @returns {string|null} domain induk, atau null bila memang belum bisa
 * diketahui — misalnya di localhost, yang tidak punya domain untuk digantungi
 * subdomain sama sekali.
 */
export function platformDomain(dariServer) {
  const disebutkan = String(dariServer || '').trim().toLowerCase().replace(/^\.+/, '')
  if (disebutkan) return disebutkan

  if (typeof window === 'undefined') return null
  const host = String(window.location.hostname || '').toLowerCase()

  if (!host || host === 'localhost' || host.endsWith('.localhost')) return null
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return null // alamat IP tidak bisa punya subdomain

  const label = host.split('.')
  // Butuh minimal domain.tld untuk bisa menampung subdomain.
  if (label.length < 2) return null
  // Buang label platform di depan: app.gladhy.co.id → gladhy.co.id
  if (label.length > 2 && LABEL_PLATFORM.has(label[0])) label.shift()

  return label.join('.')
}

/**
 * Alamat lengkap sebuah lembaga, siap ditampilkan atau diserahkan.
 * Saat domainnya belum bisa diketahui (misalnya di localhost), yang
 * dikembalikan hanya slug-nya — lebih baik daripada menampilkan domain karangan
 * yang terlihat resmi tetapi salah.
 */
export function tenantAddress(slug, dariServer) {
  const domain = platformDomain(dariServer)
  return domain ? `${slug}.${domain}` : slug
}
